/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React from 'react';
import PropTypes from 'prop-types';
import { getTranslatedExtensions } from '../utils/translate';
import ExtensionsDisplay from './ExtensionsDisplay';
import { useTranslation } from 'react-i18next';
import { useApi } from 'contexts/ApiHandler';

const SecurityDisplayFlow = ({ flowType, flow, securityValue = [], className }) => {
  const { t } = useTranslation();
  const { apiHandler } = useApi();

  let customExtensions = apiHandler.getCustomExtensions(flow);

  return (
    <div data-testid={'security-display-flow'} className={className}>
      <div key={flowType + '-flow'} className="securityFlow keyValueDisplay">
        <div className="key">{t('explorer_security_flow')}</div>
        <div className="value">{flowType}</div>
      </div>
      {(flowType === 'implicit' || flowType === 'authorizationCode') && (
        <div key={flowType + '-authUrl'} className="securityAuthUrl keyValueDisplay" data-testid={'implicit-flow'}>
          <div className="key">{t('explorer_authorization_url')}</div>
          <div className="value">
            {Array.isArray(flow.authorizationUrl) && (
              <div key="authUrl" data-testid="authUrls">
                {flow.authorizationUrl.map((url) => {
                  return (
                    <div key={url} className="url">
                      {url}
                    </div>
                  );
                })}
              </div>
            )}
            {!Array.isArray(flow.authorizationUrl) && <div>{flow.authorizationUrl}</div>}
          </div>
        </div>
      )}
      {flowType !== 'implicit' && (
        <div key={flowType + '-tokenUrl'} className="securityTokenUrl keyValueDisplay" data-testid={'non-implicit-flow'}>
          <div className="key">{t('explorer_token_url')}</div>
          <div className="value">
            {Array.isArray(flow.tokenUrl) && (
              <div data-testid="tokenUrls">
                {flow.tokenUrl.map((url) => {
                  return (
                    <div key={url} className="url">
                      {url}
                    </div>
                  );
                })}
              </div>
            )}
            {!Array.isArray(flow.tokenUrl) && <div>{flow.tokenUrl}</div>}
          </div>
        </div>
      )}
      {flow.refreshUrl && flow.refreshUrl.length > 0 && (
        <div key={flowType + '-refreshUrl'} className="securityRefreshUrl keyValueDisplay">
          <div className="key">{t('explorer_refresh_url')}</div>
          <div className="value">
            {Array.isArray(flow.refreshUrl) && (
              <div data-testid="refreshUrls">
                {flow.refreshUrl.map((url) => {
                  return (
                    <div key={url} className="url">
                      {url}
                    </div>
                  );
                })}
              </div>
            )}
            {!Array.isArray(flow.refreshUrl) && <div>{flow.refreshUrl}</div>}
          </div>
        </div>
      )}
      {flow.scopes && Object.keys(flow.scopes).length > 0 && securityValue && securityValue.length > 0 && (
        <div className="securityScopes keyValueDisplay">
          <div className="key">{t('explorer_scopes')}</div>
          <div className="value">
            {Object.keys(flow.scopes).map(function (scope) {
              // Strip off extensions
              if (scope.indexOf('$$') > -1 || scope.indexOf('x-') > -1 || securityValue.indexOf(scope) === -1) {
                return null;
              }

              let scopeElement = (
                <div key={scope} className="scope">
                  {scope}
                </div>
              );
              if (flow.scopes[scope]) {
                scopeElement = (
                  <div key={scope} className="scope">
                    {scope} - {getTranslatedExtensions(flow.scopes, scope)}
                  </div>
                );
              }

              return scopeElement;
            })}
          </div>
        </div>
      )}
      {customExtensions && <ExtensionsDisplay label="" className="securityExtension" extensions={customExtensions} />}
    </div>
  );
};

SecurityDisplayFlow.propTypes = {
  flowType: PropTypes.string.isRequired, // the security definition flow type e.g. implicit
  flow: PropTypes.object.isRequired, // the security flow object.
  securityValue: PropTypes.array,
  className: PropTypes.string
};

export default SecurityDisplayFlow;
